/**
 * Notification channels & rules (ported from CC `Channels.tsx` onto
 * the fetch fleet client).
 */
import { useState } from "react";
import {
  Badge, Button, Group, JsonInput, Modal, MultiSelect, Paper, Select,
  Stack, Switch, Tabs, Text, TextInput, Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { fleetApi } from "../../api/fleet";

export function FleetChannelsPage() {
  const qc = useQueryClient();
  const [chanOpen, setChanOpen] = useState(false);
  const [ruleOpen, setRuleOpen] = useState(false);

  // Channel form
  const [chLabel, setChLabel] = useState("");
  const [chType, setChType] = useState<string>("telegram");
  const [chConfig, setChConfig] = useState("{}");

  // Rule form
  const [rLabel, setRLabel] = useState("");
  const [rKind, setRKind] = useState("");
  const [rTemplate, setRTemplate] = useState("Agent {{agent_id}} fired {{kind}} at {{ts}}");
  const [rChannels, setRChannels] = useState<string[]>([]);
  const [rCooldown, setRCooldown] = useState("60");

  const channelsQ = useQuery({
    queryKey: ["fleet", "channels"],
    queryFn: () => fleetApi.notifications.channels.list(),
  });
  const rulesQ = useQuery({
    queryKey: ["fleet", "rules"],
    queryFn: () => fleetApi.notifications.rules.list(),
  });
  const channels = channelsQ.data;
  const rules = rulesQ.data;

  const createChannel = useMutation({
    mutationFn: () =>
      fleetApi.notifications.channels.create({
        label: chLabel, type: chType, config: JSON.parse(chConfig) as Record<string, unknown>,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["fleet", "channels"] });
      setChanOpen(false);
      notifications.show({ color: "green", message: "Channel created." });
    },
    onError: () => notifications.show({ color: "red", message: "Failed to create channel." }),
  });
  const deleteChannel = useMutation({
    mutationFn: (id: string) => fleetApi.notifications.channels.remove(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["fleet", "channels"] }),
  });
  const toggleChannel = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      fleetApi.notifications.channels.update(id, { enabled }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["fleet", "channels"] }),
  });

  const createRule = useMutation({
    mutationFn: () =>
      fleetApi.notifications.rules.create({
        label: rLabel,
        event_kind: rKind || null,
        channel_ids: rChannels,
        message_template: rTemplate,
        cooldown_seconds: parseInt(rCooldown, 10),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["fleet", "rules"] });
      setRuleOpen(false);
      notifications.show({ color: "green", message: "Rule created." });
    },
    onError: () => notifications.show({ color: "red", message: "Failed to create rule." }),
  });
  const deleteRule = useMutation({
    mutationFn: (id: string) => fleetApi.notifications.rules.remove(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["fleet", "rules"] }),
  });
  const toggleRule = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      fleetApi.notifications.rules.update(id, { enabled }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["fleet", "rules"] }),
  });

  return (
    <>
      <Title order={2} mb="lg">Channels & Rules</Title>

      <Tabs defaultValue="channels">
        <Tabs.List mb="md">
          <Tabs.Tab value="channels">Notification Channels</Tabs.Tab>
          <Tabs.Tab value="rules">Rules</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="channels">
          <Group justify="flex-end" mb="sm">
            <Button size="sm" leftSection={<IconPlus size={14} />} onClick={() => setChanOpen(true)}>
              Add Channel
            </Button>
          </Group>
          <Stack>
            {channels?.map((ch) => (
              <Paper key={ch.id} p="sm" withBorder>
                <Group justify="space-between">
                  <Group>
                    <Switch
                      checked={ch.enabled}
                      onChange={(e) => toggleChannel.mutate({ id: ch.id, enabled: e.currentTarget.checked })}
                    />
                    <Stack gap={0}>
                      <Text fw={500}>{ch.label}</Text>
                      <Badge size="xs" variant="light">{ch.type}</Badge>
                    </Stack>
                  </Group>
                  <Button size="xs" variant="subtle" color="red" onClick={() => deleteChannel.mutate(ch.id)}>
                    <IconTrash size={14} />
                  </Button>
                </Group>
              </Paper>
            ))}
            {!channels?.length && <Text c="dimmed">No channels yet.</Text>}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="rules">
          <Group justify="flex-end" mb="sm">
            <Button size="sm" leftSection={<IconPlus size={14} />} onClick={() => setRuleOpen(true)}>
              Add Rule
            </Button>
          </Group>
          <Stack>
            {rules?.map((rule) => (
              <Paper key={rule.id} p="sm" withBorder>
                <Group justify="space-between">
                  <Group>
                    <Switch
                      checked={rule.enabled}
                      onChange={(e) => toggleRule.mutate({ id: rule.id, enabled: e.currentTarget.checked })}
                    />
                    <Stack gap={0}>
                      <Text fw={500}>{rule.label}</Text>
                      <Text size="xs" c="dimmed">
                        {rule.event_kind ?? "all events"} · cooldown {rule.cooldown_seconds}s · {rule.channel_ids.length} channel(s)
                      </Text>
                    </Stack>
                  </Group>
                  <Button size="xs" variant="subtle" color="red" onClick={() => deleteRule.mutate(rule.id)}>
                    <IconTrash size={14} />
                  </Button>
                </Group>
              </Paper>
            ))}
            {!rules?.length && <Text c="dimmed">No rules yet.</Text>}
          </Stack>
        </Tabs.Panel>
      </Tabs>

      {/* Add Channel Modal */}
      <Modal opened={chanOpen} onClose={() => setChanOpen(false)} title="Add Notification Channel">
        <Stack>
          <TextInput label="Label" value={chLabel} onChange={(e) => setChLabel(e.currentTarget.value)} required />
          <Select
            label="Type"
            data={["telegram", "webhook", "email"]}
            value={chType}
            onChange={(v) => setChType(v ?? "telegram")}
          />
          <JsonInput label="Config JSON" value={chConfig} onChange={setChConfig} formatOnBlur minRows={4} />
          <Text size="xs" c="dimmed">
            Telegram: {`{"bot_token":"...", "chat_id":"..."}`}<br />
            Webhook: {`{"url":"https://..."}`}<br />
            Email: {`{"to":"you@example.com"}`}
          </Text>
          <Button onClick={() => createChannel.mutate()} loading={createChannel.isPending}>Create</Button>
        </Stack>
      </Modal>

      {/* Add Rule Modal */}
      <Modal opened={ruleOpen} onClose={() => setRuleOpen(false)} title="Add Rule">
        <Stack>
          <TextInput label="Label" value={rLabel} onChange={(e) => setRLabel(e.currentTarget.value)} required />
          <TextInput
            label="Event kind (blank = all bot events)"
            description="Bot events: bot_error, feed_disconnected, order_filled, window_resolved… System events must be named explicitly: storage_alert (Settings → Storage)."
            value={rKind}
            onChange={(e) => setRKind(e.currentTarget.value)}
            placeholder="bot_error"
          />
          <TextInput label="Message template" value={rTemplate} onChange={(e) => setRTemplate(e.currentTarget.value)} />
          <MultiSelect
            label="Channels"
            data={channels?.map((c) => ({ value: c.id, label: c.label })) ?? []}
            value={rChannels}
            onChange={setRChannels}
          />
          <TextInput label="Cooldown (seconds)" value={rCooldown} onChange={(e) => setRCooldown(e.currentTarget.value)} />
          <Button onClick={() => createRule.mutate()} loading={createRule.isPending}>Create</Button>
        </Stack>
      </Modal>
    </>
  );
}
